import { useMemo, useState } from "react";
import type { Lead, TextShortcut, WhatsappChat } from "../../../types.ts";
import { SvgIcon } from "../../chat-list/ChatAvatar.tsx";
import {
  expandShortcutText,
  normalizeShortcutTrigger,
  shortcutPreview,
  shortcutSearchText,
} from "../../Shortcuts.helpers.ts";
import { icons } from "../icons.ts";

type ShortcutPickerProps = {
  chat: WhatsappChat;
  lead: Lead | null;
  shortcuts: TextShortcut[];
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onLoadShortcuts: () => Promise<TextShortcut[]>;
  onApplyText: (text: string) => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
};

export function ShortcutPicker({
  chat,
  lead,
  shortcuts,
  open,
  onToggle,
  onClose,
  onLoadShortcuts,
  onApplyText,
  onError,
  onSuccess,
}: ShortcutPickerProps) {
  const [shortcutQuery, setShortcutQuery] = useState("");
  const [shortcutLoading, setShortcutLoading] = useState(false);

  const filteredShortcuts = useMemo(() => {
    const search = shortcutQuery.trim().toLowerCase();
    const items = search ? shortcuts.filter((item) => shortcutSearchText(item).includes(search)) : shortcuts;
    return [...items].sort((a, b) => {
      if (a.scope !== b.scope) return a.scope === "personal" ? -1 : 1;
      return normalizeShortcutTrigger(a.trigger).localeCompare(normalizeShortcutTrigger(b.trigger));
    });
  }, [shortcuts, shortcutQuery]);

  async function toggleShortcutPicker() {
    const nextOpen = !open;
    onToggle();
    if (!nextOpen || shortcuts.length) return;
    setShortcutLoading(true);
    try {
      await onLoadShortcuts();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Falha ao carregar atalhos");
    } finally {
      setShortcutLoading(false);
    }
  }

  function applyShortcut(shortcut: TextShortcut) {
    const expanded = expandShortcutText(shortcut.body, { lead, chat });
    if (!expanded) return;
    onApplyText(expanded);
    onClose();
    setShortcutQuery("");
    onSuccess(`Atalho ${shortcut.trigger} inserido`);
  }

  return (
    <div className="wa-shortcut-anchor">
      <button className="wa-icon-btn" type="button" title="Atalhos" aria-label="Atalhos" onClick={() => void toggleShortcutPicker()}>
        <SvgIcon svg={icons.shortcuts} />
      </button>
      {open ? (
        <div className="wa-shortcut-popover" role="dialog" aria-label="Selecionar atalho">
          <header className="wa-shortcut-popover-head">
            <strong>Atalhos</strong>
            <button className="wa-popover-close" type="button" aria-label="Fechar atalhos" onClick={onClose}>
              x
            </button>
          </header>
          <input
            className="wa-shortcut-search"
            placeholder="Buscar /pix..."
            value={shortcutQuery}
            onChange={(event) => setShortcutQuery(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") onClose();
            }}
          />
          <div className="wa-shortcut-results">
            {shortcutLoading ? <div className="wa-shortcut-empty">Carregando...</div> : null}
            {!shortcutLoading && !filteredShortcuts.length ? <div className="wa-shortcut-empty">Nenhum atalho salvo</div> : null}
            {filteredShortcuts.map((shortcut) => (
              <button key={shortcut.id} className="wa-shortcut-option" type="button" onClick={() => applyShortcut(shortcut)}>
                <span>
                  <strong>{shortcut.trigger}</strong>
                  <small>{shortcut.title || shortcut.trigger}</small>
                </span>
                <em>{shortcutPreview(shortcut)}</em>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
